import { useState } from 'react';
import { Popover } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from './Icon';
import { IconButton } from './IconButton';
import {
  relativeTime,
  useNotifications,
  type Notification,
} from '@/store/notifications';

/**
 * Bell button in the topbar that opens a popover with the in-memory
 * notifications list. The popover renders MAFA's "notif" visual: a
 * coloured icon badge per item, a title, a short body and a relative
 * timestamp. Clicking an item navigates to the related context (if
 * provided) and marks it read.
 */
export function NotificationsButton() {
  const { t } = useTranslation();
  const [opened, setOpened] = useState(false);
  const navigate = useNavigate();
  const items = useNotifications((s) => s.items);
  const unread = useNotifications((s) => s.unreadCount);
  const markAllRead = useNotifications((s) => s.markAllRead);

  function open(n: Notification) {
    markAllRead();
    setOpened(false);
    if (n.href) {
      void navigate({ to: n.href });
    }
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      withArrow={false}
      shadow="xl"
      offset={8}
    >
      <Popover.Target>
        <IconButton
          name="bell"
          withDot={unread > 0}
          onClick={() => setOpened((v) => !v)}
          aria-label={t('topbar.notifications') ?? 'Notifications'}
        />
      </Popover.Target>
      <Popover.Dropdown
        styles={{
          dropdown: {
            width: 380,
            padding: 0,
            border: '1px solid var(--line)',
            borderRadius: 16,
            background: 'var(--panel)',
          },
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '15px 18px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <span style={{ fontWeight: 800, fontSize: 14.5 }}>{t('notifications.title')}</span>
          {unread > 0 && (
            <span className="meta acc" style={{ marginLeft: 'auto' }}>
              <span className="sq" />
              {t('notifications.newCount', {
                count: unread,
                plural: unread > 1 ? 's' : '',
              })}
            </span>
          )}
        </div>

        {items.length === 0 ? (
          <div className="muted" style={{ padding: 28, textAlign: 'center', fontSize: 13 }}>
            {t('notifications.empty')}
          </div>
        ) : (
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => open(n)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  width: '100%',
                  cursor: 'pointer',
                  textAlign: 'left',
                  padding: '13px 18px',
                  border: 'none',
                  borderBottom: '1px solid var(--line)',
                  background: n.unread ? 'var(--accent-tint)' : 'transparent',
                  transition: 'background .12s',
                  fontFamily: 'var(--ff)',
                }}
              >
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: kindBg(n.kind),
                    border: `1px solid ${kindBorder(n.kind)}`,
                    color: kindText(n.kind),
                    display: 'grid',
                    placeItems: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  <Icon name={n.icon as IconName} size={17} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 700, fontSize: 13.5 }}>{n.title}</span>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 12,
                      color: 'var(--ink-3)',
                      fontWeight: 500,
                      marginTop: 2,
                      lineHeight: 1.4,
                    }}
                  >
                    {n.body}
                  </span>
                </span>
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--ink-4)',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    flex: '0 0 auto',
                  }}
                >
                  {relativeTime(n.occurredAt)}
                </span>
              </button>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div
            style={{
              padding: '11px 18px',
              display: 'flex',
              justifyContent: 'center',
              borderTop: '1px solid var(--line)',
            }}
          >
            <button
              className="btn ghost sm"
              onClick={markAllRead}
              disabled={unread === 0}
              style={{ opacity: unread === 0 ? 0.5 : 1 }}
            >
              {t('notifications.markAllRead')}
            </button>
          </div>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}

function kindBg(k: Notification['kind']): string {
  if (k === 'success') return 'var(--accent-tint)';
  if (k === 'warning') return 'var(--alert-tint)';
  return 'var(--panel-2)';
}

function kindBorder(k: Notification['kind']): string {
  if (k === 'success') return 'var(--accent-tint-2)';
  if (k === 'warning') return 'var(--alert-tint-2)';
  return 'var(--line)';
}

function kindText(k: Notification['kind']): string {
  if (k === 'success') return 'var(--accent-2)';
  if (k === 'warning') return 'var(--alert-ink)';
  return 'var(--ink-3)';
}
